import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

const Articles = () => {
  const articles = [
    {
      title: 'Building Scalable Applications with Microservices',
      excerpt: 'Learn how to design and implement microservices architecture for modern applications.',
      date: '2024-01-15',
      readTime: '8 min read',
      category: 'Architecture',
      slug: 'microservices-architecture'
    },
    {
      title: 'The Future of Web Development with React and AI',
      excerpt: 'Exploring how artificial intelligence is transforming the React ecosystem.',
      date: '2024-01-10',
      readTime: '6 min read',
      category: 'AI & Development',
      slug: 'react-ai-future'
    },
    {
      title: 'Smart Contract Security Best Practices',
      excerpt: 'Essential security considerations when developing blockchain smart contracts.',
      date: '2024-01-05',
      readTime: '10 min read',
      category: 'Blockchain',
      slug: 'smart-contract-security'
    },
    {
      title: 'Project Management in Tech: Lessons Learned',
      excerpt: 'Key insights from managing technical projects in fast-paced environments.',
      date: '2023-12-28',
      readTime: '7 min read',
      category: 'Management',
      slug: 'tech-project-management'
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom max-w-6xl">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center mb-16"
        >
          <h1 className="mb-6">Articles</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            In-depth technical articles, tutorials, and insights on software development, 
            architecture, and emerging technologies.
          </p>
        </motion.div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {articles.map((article, index) => (
            <motion.article
              key={article.slug}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: index * 0.1 }}
              className="card group"
            >
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                  {article.category}
                </span>
              </div>
              
              <h3 className="text-2xl font-display font-semibold mb-3 group-hover:text-[rgb(var(--color-accent))] transition-colors">
                {article.title}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {article.excerpt}
              </p>
              
              <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-500 mb-4">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <Calendar size={16} className="mr-1" />
                    {new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    {article.readTime}
                  </span>
                </div>
              </div>
              
              <Link
                to={`/blog/${article.slug}`}
                className="inline-flex items-center text-[rgb(var(--color-accent))] font-medium hover:underline"
              >
                Read Article
                <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Articles;
